import { Box } from '@chakra-ui/react'
import React, { useMemo, useState, useEffect } from 'react'
import { useWatch } from 'react-hook-form'

import { resolveSubSystemMargin } from '@eapproval/offer-shared'

import { getTotalPrice } from '@/features/offers/helpers/getTotalContractPrice'
import { sumArray } from '@/utils/arrayHelper'
import { numberWithCommas, roundNumber } from '@/utils/stringHelper'
import EditableTable from '@/components/Table/EditableTable'
import { createColumnHelper } from '@tanstack/react-table'
import { NormalTextCellRenderer } from '@/components/Table/components/Cell/ToolTipCell'
import { PriceCell } from '@/components/Table/components/Cell'

const PricingSummaryTable = () => {
  const subSystems = useWatch({ name: 'subSystems' })
  const currency = useWatch({ name: 'currency' })
  const exchangeRate = useWatch({ name: 'exchangeRate' })

  const columnHelper = createColumnHelper()

  const data = useMemo(() => {
    if (!subSystems) return []

    return subSystems.map((subSystem, index) => {
      const offerPrice = getTotalPrice(subSystem.totalContractPrice.newContractRows)
      const offerPriceHKD = offerPrice * (parseFloat(exchangeRate) || 1)
      const negotiationMargin = subSystem.totalContractPrice.negotiationMargin * -1 || 0

      const { cost, gmAmount, gmPercentage } = resolveSubSystemMargin({
        priceHKD: offerPriceHKD,
        totalCost: subSystem.totalContractPrice.totalCost,
        storedGrossMargin: subSystem.totalContractPrice.grossMargin,
      })

      const withoutMarginPrice = (offerPriceHKD * (100 - (negotiationMargin || 0))) / 100

      return {
        name: `1.${index + 1}: ${subSystem.projectName}`,
        type: subSystem.type,
        offerPrice: offerPrice,
        currency: currency,
        withMargin: {
          price: offerPriceHKD,
          GM: gmPercentage,
          GMPrice: gmAmount,
        },
        withoutMargin: {
          price: withoutMarginPrice,
          GM: ((withoutMarginPrice - cost) / withoutMarginPrice) * 100 || 0,
          GMPrice: withoutMarginPrice - cost,
        },

        profitCenter: subSystem?.profitCenter?.profitCenterCode || '',
      }
    })
  }, [subSystems, currency, exchangeRate])

  const columnDef = useMemo(
    () => [
      columnHelper.group({
        id: 'generalInfo',
        header: () => <span></span>,
        columns: [
          columnHelper.accessor('name', {
            header: () => <span></span>,
          }),
          columnHelper.accessor('type', {
            cell: NormalTextCellRenderer,
          }),
          columnHelper.accessor('offerPrice', {
            cell: ({ getValue, row: { original } }) => (
              <PriceCell currency={original.currency} getValue={getValue} />
            ),
          }),
        ],
      }),

      columnHelper.group({
        id: 'withNegotiationMargin',

        header: () => <span>With Negotiation Margin</span>,
        columns: [
          columnHelper.accessor('withMargin.price', {
            header: () => <span>Total Price(HKD)</span>,
            cell: PriceCell,
            footer: ({ table, column: { id: columnId } }) => {
              const totalSubSystemPrice = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue(columnId))
              )

              return <span>${numberWithCommas(totalSubSystemPrice)}</span>
            },
          }),
          columnHelper.accessor('withMargin.GM', {
            header: () => <span>GM (%)</span>,

            cell: ({ getValue }) => <span>{roundNumber(getValue(), 2)}%</span>,
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('withMargin_GMPrice'))
              )
              const priceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('withMargin_price'))
              )

              if (priceTotal === 0) {
                return <span>0.00%</span>
              }

              const GMPercentageTotal = roundNumber((GMPriceTotal / priceTotal) * 100, 2)

              return <span>{GMPercentageTotal}%</span>
            },
          }),
          columnHelper.accessor('withMargin.GMPrice', {
            header: () => <span>GM (HKD)</span>,
            cell: PriceCell,
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('withMargin_GMPrice'))
              )

              return <span>${numberWithCommas(GMPriceTotal)}</span>
            },
          }),
        ],
      }),
      columnHelper.group({
        id: 'withoutNegotiationMargin',
        header: () => <span>Without Negotiation Margin</span>,
        backgroundColor: '#d3dedd',
        columns: [
          columnHelper.accessor('withoutMargin.price', {
            header: () => <span>Total Price(HKD)</span>,
            backgroundColor: '#d3dedd',
            cell: PriceCell,
            footer: ({ table, column: { id: columnId } }) => {
              const totalSubSystemPrice = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue(columnId))
              )

              return <span>${numberWithCommas(totalSubSystemPrice)}</span>
            },
          }),
          columnHelper.accessor('withoutMargin.GM', {
            header: () => <span>GM (%)</span>,
            backgroundColor: '#d3dedd',
            cell: ({ getValue }) => <span>{roundNumber(getValue(), 2)}%</span>,
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('withoutMargin_GMPrice'))
              )
              const priceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('withoutMargin_price'))
              )

              if (priceTotal === 0) {
                return <span>0.00%</span>
              }

              const GMPercentageTotal = roundNumber((GMPriceTotal / priceTotal) * 100, 2)

              return <span>{GMPercentageTotal}%</span>
            },
          }),
          columnHelper.accessor('withoutMargin.GMPrice', {
            header: () => <span>GM (HKD)</span>,
            backgroundColor: '#d3dedd',
            cell: PriceCell,
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('withoutMargin_GMPrice'))
              )
              return <span>${numberWithCommas(GMPriceTotal)}</span>
            },
          }),
        ],
      }),
    ],
    [columnHelper]
  )

  return <EditableTable columnDef={columnDef} data={data} disabled={true} showFooter tableWidth="100%" />
}

export default PricingSummaryTable
